import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Button, CircularProgress, Dialog, DialogContent, DialogTitle, DialogContentText, DialogActions, IconButton, Toolbar } from "@mui/material";
import { DataTable } from "../../components/DataTable";
import CloseIcon from "@mui/icons-material/Close";
import { List } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";

import { ApiDelete, ApiGet } from "../../helpers/API/ApiData";

import { ReviewsAndRating } from "./ReviewsAndRating";
import { EditSurveyModal } from "./EditSurveyModal";

import { getColumns } from "./getColumns";
import { usePermissions } from "../../hooks/usePermissions";
import { useContentLanguage } from "../../hooks/useContentLanguage";
import { PageHeader } from "../../components/PageHeader";
import { LanguageTabs } from "../../components/LanguageTabs";

import "./survey.scss";

const Survey = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/survey");
  const [lang, setLang] = useContentLanguage();
  const [page, setPage] = useState(1);
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [countPerPage, setCountPerPage] = useState(10);
  const [surveyData, setSurveyData] = useState<any>();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [surveyObj, setSurveyObj] = useState<any>({});
  const [isEditMode, setEditMode] = useState(false);
  const [idForSurveyEdit, setIdForSurveyEdit] = useState("");
  const [search, setSearch] = useState("");
  const [editSurveyModalOpen, setEditSurveyModalOpen] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSurveyDetails, setShowSurveyDetails] = useState(false);
  const [surveyIdForDelete, setSurveyIdForDelete] = useState("");

  const [surveyUserDetailsData, setSurveyUserDetailsData] = useState<any>([]);

  const [inputValue, setInputValue] = useState<any>({});

  const handleSurveyModalClose = () => {
    setEditSurveyModalOpen(false);
    setEditMode(false);

    setInputValue({});
  };

  const handleClose = () => {
    setShowDeleteModal(false);
    setShowSurveyDetails(false);
    setSurveyObj({});
  };

  useEffect(() => {
    getSurveyDetailsData();
  }, [page, countPerPage, search]);

  const getSurveyDetailsData = async () => {
    setLoadingData(true);
    try {
      const response = await ApiGet(
        `survey/find?letter=${search}&page=${page}&limit=${countPerPage}`
      );
      setLoadingData(false);
      setSurveyData(response?.data?.data);
      setTotalRowsCount(response?.data?.count ?? 0);
    } catch (err: any) {
      console.error("getSurveyDetailsData | error", err);
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
    } finally {
      setLoadingData(false);
    }
  };

  const deleteSurveyDetail = async () => {
    try {
      await ApiDelete(`survey/delete?id=${surveyIdForDelete}`);
      setShowDeleteModal(false);
      toast.success("Umfragen erfolgreich löschen");
      setPage(1);
      getSurveyDetailsData();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
    }
  };

  const getSurveyUserDetailsData = async (row: any) => {
    setLoading(true);
    await ApiGet(`survey/getSurveyUserDetails?id=${row}`)
      .then((res: any) => {
        setSurveyUserDetailsData(res?.data?.data[0]);
        setLoading(false);
      })
      .catch((err: any) => {
        console.error("getSurveyCountData | error", err);
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
        setLoading(false);
      });
  };


  const updateSurveyClickHandler = ({ row }: { row: any }) => {
    setEditMode(true);
    setEditSurveyModalOpen(true);
    setIdForSurveyEdit(row?._id);
    setInputValue({
      surveyQuestion: row?.surveyQuestion,
      surveyQuestion_ger: row?.surveyQuestion_ger,
      surveyField: row?.surveyField,
      surveyType: row?.surveyType,
      surveyOption: row?.surveyOption,
      surveyOption_ger: row?.surveyOption_ger,
      coin: row?.coin,
    });
  };

  const deleteSurveyClickHandler = ({ row }: { row: any }) => {
    setSurveyIdForDelete(row?._id);
    setShowDeleteModal(true);
  };

  const surveyDetailsClickHandler = ({ row }: { row: any }) => {
    setSurveyObj(row);
    setShowSurveyDetails(true);
    getSurveyUserDetailsData(row?._id);
  };

  const columns = getColumns({
    updateSurveyClickHandler,
    deleteSurveyClickHandler,
    surveyDetailsClickHandler,
    canEdit,
    canDelete,
    lang,
  });

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <PageHeader
          title="Umfragen"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => { setEditSurveyModalOpen(true); } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        <Box sx={{ p: 2, mb: 2 }}>
          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              rows={surveyData || []}
              columns={columns}
              getRowId={(row: any) => row._id}
              page={page}
              count={totalRowsCount}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
              rowsPerPageOptions={[10, 20, 25, 50, 100]}
            />
          )}
        </Box>
      </Paper>

      <Dialog open={showDeleteModal} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diesen Privatsphäre löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => deleteSurveyDetail()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      {showSurveyDetails && (
        <Dialog
          open={showSurveyDetails}
          style={{ overflow: "hidden" }}
          maxWidth={surveyObj?.surveyType === "option" ? "lg" : "md"}
        >
          <Toolbar>
            <IconButton
              edge="end"
              style={{ marginLeft: "97%" }}
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <DialogContent
            style={{
              width: "700px",
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ p: 0, textAlign: "center" }}>
                {surveyObj?.surveyType === "option" && "Details zur Options umfrage" }
                {surveyObj?.surveyType === "star" && "Details zur Stern umfrage" }
                {surveyObj?.surveyType === "text" && "Details zur Text umfrage" }
              </Typography>
              <Box sx={{ mt: 8 }}>
                <ReviewsAndRating
                  loading={loading}
                  surveyUserDetailsData={surveyUserDetailsData}
                  surveyType={surveyObj?.surveyType}
                />
              </Box>{" "}
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {editSurveyModalOpen && (
        <EditSurveyModal
          isEditMode={isEditMode}
          idForSurveyEdit={idForSurveyEdit}
          inputValue={inputValue}
          editSurveyModalOpen={editSurveyModalOpen}
          setEditMode={setEditMode}
          setInputValue={setInputValue}
          getSurveyDetails={getSurveyDetailsData}
          setEditSurveyModalOpen={setEditSurveyModalOpen}
          handleSurveyModalClose={handleSurveyModalClose}
        />
      )}
    </>
  );
};

export default Survey;
